import React, { ReactNode } from 'react';
import { Flex } from '@clariness/flex';
import { RadioButtonProps } from '@clariness/radio-button';
import { Icon } from '@clariness/clarikit-react';
import { CheckCircleIcon } from '@clariness/clarikit-icons';
import { Text } from '@clariness/clarikit-react';
import { Box } from '@clariness/box';
import { StatusType } from 'components/modals/UpdatePatientStatusModal/components/UpdatePatientMainStatus/UpdatePatientMainStatus';
import { ReferralActivityDto } from 'common/api';
import { ReferralStatus, ReferralStatusType } from 'global/referralStatus';
import { formatDate } from 'utils/format';
import { useIntl } from 'react-intl';
import { useUpdatePatientStatusContext } from 'components/modals/UpdatePatientStatusModal';

type UpdateStatusCheckboxProps = RadioButtonProps & {
  statusTitle?: ReactNode;
  statusDescription?: ReactNode;
  statuesType: StatusType;
  referralActivity?: ReferralActivityDto;
  historyAppointmentDataTime?: string;
  referralStatus: ReferralStatusType;
};

function getStyles(statusType: StatusType) {
  switch (statusType) {
    case StatusType.ACTIVE:
      return {
        box: { borderColor: 'primary1' },
      };
    case StatusType.SELECTED:
      return {
        box: {
          borderColor: 'primary2',
          backgroundColor: 'primary-2',
        },
      };
    case StatusType.UPCOMING:
      return {
        box: {
          borderColor: 'primary1',
        },
      };
    case StatusType.HISTORY:
      return {
        text: { color: '#3E3E3E' },
        box: {
          border: 'none',
          backgroundColor: '#D9D9D9',
        },
      };
    case StatusType.PAST_APPOINTMENT:
      return {
        box: { borderColor: 'red-alert' },
      };
    case StatusType.SELECTED_PAST_APPOINTMENT:
      return {
        box: {
          borderColor: 'red-alert',
          backgroundColor: '#FED8AE',
        },
      };
    default:
      return {};
  }
}

export const UpdateStatusTile: React.FC<UpdateStatusCheckboxProps> = ({
  statusTitle,
  statusDescription,
  statuesType,
  historyAppointmentDataTime,
  referralStatus,
  ...props
}) => {
  const intl = useIntl();

  const {
    appointmentDateTime,
    timezoneData,
    selectedProtocol,
    consentedDate,
    referralDate,
  } = useUpdatePatientStatusContext();

  const getAppointmentDateTime = (): string | undefined => {
    if (statuesType === StatusType.HISTORY && historyAppointmentDataTime) {
      return historyAppointmentDataTime;
    }
    return appointmentDateTime;
  };

  const style = getStyles(statuesType);

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        border: '2px solid',
        borderRadius: '8px',
        padding: '16px 10px',
        flexShrink: 0,
        width: '100%',
        cursor: 'pointer',
        alignItems: 'center',
        gap: '5px',
        margin: 0,
        ...style.box,
      }}
      {...props}
    >
      <Box sx={{ ...style.text, display: 'flex', gap: '16px' }}>
        {statuesType === StatusType.ACTIVE && (
          <Icon source={CheckCircleIcon} color="color-green-primary" />
        )}
        {(statuesType === StatusType.PAST_APPOINTMENT ||
          statuesType === StatusType.SELECTED_PAST_APPOINTMENT) && (
          <Icon source={CheckCircleIcon} color="color-red-variant-3" />
        )}
        <Text as="p" fontWeight="bold">
          {statusTitle}
        </Text>
      </Box>

      {(statuesType === StatusType.UPCOMING ||
        statuesType === StatusType.SELECTED) && (
        <Box
          sx={{
            ...style.text,
          }}
        >
          <Text as="p">{statusDescription}</Text>
        </Box>
      )}

      {referralDate && referralStatus === ReferralStatus.NEW_REFERRAL && (
        <Box
          sx={{
            ...style.text,
          }}
        >
          <Text as="p">
            {formatDate(intl.formatDate(new Date(referralDate as string)))}
          </Text>
        </Box>
      )}

      {referralStatus === ReferralStatus.APPOINTMENT_SCHEDULED &&
        getAppointmentDateTime() && (
          <Box
            sx={{
              ...style.text,
            }}
          >
            <Text as="p">
              {`
            ${new Date(getAppointmentDateTime() ?? '')?.toLocaleDateString(
              'de-DE',
              {
                timeZone: timezoneData?.siteTimeZone?.zoneName,
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              }
            )} 
          ${new Date(getAppointmentDateTime() ?? '')?.toLocaleTimeString(
            'en-US',
            {
              timeZone: timezoneData?.siteTimeZone?.zoneName,
              hour: '2-digit',
              minute: '2-digit',
            }
          )} 
          - ${timezoneData?.siteTimeZone?.zoneName}`}
            </Text>
          </Box>
        )}

      {referralStatus === ReferralStatus.CONSENTED && (
        <Box
          sx={{
            ...style.text,
          }}
        >
          <Text as="p">{selectedProtocol?.name}</Text>
          {consentedDate && (
            <Text as="p">
              {new Date(consentedDate || '')?.toLocaleDateString(
                timezoneData?.siteTimeZone?.countryCode
              )}
            </Text>
          )}
        </Box>
      )}
    </Flex>
  );
};
